import styled, { css, keyframes } from 'styled-components'
import { StyledButtonProps } from './Button.types'

const sizeStyles = {
  small: css`
    padding: 4px 8px;
    font-size: 11px;
    border-radius: ${({ theme }) => theme.borderRadius.small};
    gap: 4px;
  `,
  medium: css`
    padding: 6px 12px;
    font-size: 12px;
    border-radius: 6px;
    gap: 4px;
  `,
  large: css`
    padding: 10px 20px;
    font-size: ${({ theme }) => theme.typography.fontSize.small};
    border-radius: ${({ theme }) => theme.borderRadius.medium};
    gap: ${({ theme }) => theme.spacing.micro};
  `,
}

const variantStyles = {
  primary: css`
    background-color: ${({ theme }) => theme.colors.primary};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.primaryHover};
      box-shadow: ${({ theme }) => theme.shadows.sm};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  secondary: css`
    background-color: ${({ theme }) => theme.colors.background.primary};
    color: ${({ theme }) => theme.colors.text.primary};
    border: 1px solid ${({ theme }) => theme.colors.border.default};

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      border-color: ${({ theme }) => theme.colors.text.tertiary};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  tertiary: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.tertiary};
    border: 1px solid transparent;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.background.secondary};
      color: ${({ theme }) => theme.colors.text.secondary};
      border-color: ${({ theme }) => theme.colors.border.light};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  success: css`
    background-color: ${({ theme }) => theme.colors.success};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.success};
      box-shadow: ${({ theme }) => theme.shadows.sm};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  warning: css`
    background-color: ${({ theme }) => theme.colors.warning};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.warning};
      box-shadow: ${({ theme }) => theme.shadows.sm};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  danger: css`
    background-color: ${({ theme }) => theme.colors.danger};
    color: white;
    border: none;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.danger};
      box-shadow: ${({ theme }) => theme.shadows.sm};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
  ghost: css`
    background-color: transparent;
    color: ${({ theme }) => theme.colors.text.secondary};
    border: 1px solid transparent;

    &:hover:not(:disabled) {
      background-color: ${({ theme }) => theme.colors.background.tertiary};
      color: ${({ theme }) => theme.colors.text.primary};
    }

    &:active:not(:disabled) {
      transform: translateY(1px);
    }
  `,
}

const spin = keyframes`
  0% { transform: rotate(0deg); }
  100% { transform: rotate(360deg); }
`

export const StyledButton = styled.button<StyledButtonProps>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-family: ${({ theme }) => theme.typography.fontFamily.sans};
  font-weight: ${({ theme }) => theme.typography.fontWeight.medium};
  cursor: pointer;
  transition: all ${({ theme }) => theme.transitions.fast};
  text-decoration: none;
  white-space: nowrap;
  line-height: 1;
  width: ${(props) => (props.$fullWidth ? '100%' : 'auto')};
  border: none;
  position: relative;

  ${(props) => sizeStyles[props.$size]}
  ${(props) => variantStyles[props.$variant]}

  &:focus {
    outline: 2px solid ${({ theme }) => theme.colors.border.focus};
    outline-offset: 2px;
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }

  ${(props) =>
    props.$loading &&
    css`
      color: transparent !important;
      pointer-events: none;

      &::after {
        content: '';
        position: absolute;
        width: 16px;
        height: 16px;
        top: 50%;
        left: 50%;
        margin-left: -8px;
        margin-top: -8px;
        border: 2px solid
          ${props.$variant === 'primary' ||
          props.$variant === 'success' ||
          props.$variant === 'warning' ||
          props.$variant === 'danger'
            ? '#f3f3f3'
            : props.theme.colors.border.default};
        border-top: 2px solid
          ${props.$variant === 'primary' ||
          props.$variant === 'success' ||
          props.$variant === 'warning' ||
          props.$variant === 'danger'
            ? 'currentColor'
            : props.theme.colors.primary};
        border-radius: 50%;
        animation: ${spin} 1s linear infinite;
      }
    `}
`

export const IconWrapper = styled.span`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
`
